import type { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";

interface RoleGuardProps {
  children: ReactNode;
  requiredRoleName?: string;
  requiredAnyRole?: string[];
  adminOnly?: boolean;
  fallback?: ReactNode;
}

export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  requiredRoleName,
  requiredAnyRole,
  adminOnly = false,
  fallback = null,
}) => {
  const { userProfile, isAdmin, loading, hasRole, hasAnyRole } = useAuth();

  // If still loading authentication, don't show fallback yet
  if (loading) {
    return null;
  }

  // If no user profile is loaded after loading is complete, show fallback
  if (!userProfile) {
    return <>{fallback}</>;
  }

  // If adminOnly is true, only admins can see this content
  if (adminOnly && !isAdmin) {
    return <>{fallback}</>;
  }

  // If a specific role name is required (RBAC), check against user's rbac_roles
  if (requiredRoleName && !hasRole(requiredRoleName)) {
    return <>{fallback}</>;
  }

  // If any of multiple roles are required, check against user's rbac_roles
  if (requiredAnyRole && !hasAnyRole(requiredAnyRole)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};
